<script lang="ts">
export const elInputSearchColors = ['primary', 'secondary'] as const;
export type ElInputSearchColors = (typeof elInputSearchColors)[number];

export const elInputSearchStatus = ['default', 'filled', 'blank'] as const;
export type ElInputSearchStatus = (typeof elInputSearchStatus)[number];
</script>

<script lang="ts" setup>
import ElDivider from '@/ElDivider.vue';
import ElIcon from '@/ElIcon.vue';
import ElVerticalTab from '@/ElVerticalTab.vue';
import { ElButton } from '..';
import { computed, PropType, ref, useSlots, watch, nextTick } from 'vue';
import { useInput, withTextualElInputProps } from './input';
import { vOnClickOutside } from '@vueuse/components';
import CustomTransition from '@/_CustomTransition.vue';
import { onKeyStroke, useScrollLock } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

const props = defineProps({
  ...withTextualElInputProps(),
  status: {
    type: String as PropType<ElInputSearchStatus>,
    default: 'default',
  },
  text: {
    type: String,
    default: undefined,
  },
  results: {
    type: Array as PropType<InstanceType<typeof ElVerticalTab>['$props'][]>,
    default: undefined,
  },
  footer: {
    type: Object as PropType<{
      text: string;
      button: InstanceType<typeof ElButton>['$props'];
    }>,
    default: undefined,
  },
  blankResults: {
    type: Object as PropType<{
      icon: InstanceType<typeof ElIcon>['$props'];
      primaryRow: string;
      secondaryRow?: string;
    }>,
    default: undefined,
  },
  shouldHoverFirstResultOnOpen: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String as PropType<ElInputSearchColors>,
    default: 'primary',
  },
  hideSearchBar: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'onResultClick', 'onResultHover']);
const slots = useSlots();
const isBodyScrollLocked = useScrollLock(document.body);

const searchInput = ref<HTMLInputElement | null>(null);
const computedModelValue = computed(() => props.modelValue);
const computedValidation = computed(() => [props.validation]);
const computedStatus = computed(() => ({
  default: !props.results,
  filled: props.results && props.results.length > 0,
  blank: props.results && props.results.length === 0,
}));
const isClickOutside = ref(false);
const isSearchFocused = ref(false);

const itemRefs = ref<InstanceType<typeof ElVerticalTab>[]>([]);
const activeResult = ref<number | undefined>();

const { value, uuid, setValue } = useInput(computedModelValue, computedValidation, props.name, newValue => {
  emit('update:modelValue', newValue?.trim());
});

const searchBarRef = ref<HTMLInputElement>();
const { hasFocus, activate, deactivate } = useFocusTrap(searchBarRef as any);

const isOpen = defineModel('isOpen', {
  default: true,
  required: true,
  type: Boolean,
});

const closeSearchBar = () => {
  isOpen.value = false;
  activeResult.value = undefined;
  value.value = '';
};

const scrollToFocusedIndex = (index: number) => {
  itemRefs.value?.at(index)?.$el?.scrollIntoView({ block: 'nearest' });
};

const onResultClick = (index: number) => {
  emit('onResultClick', index);
  setValue('');
  activeResult.value = undefined;
};

const onResultHover = (index: number) => {
  activeResult.value = index;
  scrollToFocusedIndex(index);
  emit('onResultHover', index);
};

const onClickOutside = () => closeSearchBar();

onKeyStroke(
  ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'],
  e => {
    e.preventDefault();
    e.stopPropagation();

    if (hasFocus && props.results?.length) {
      switch (e.key) {
        case 'ArrowUp':
          const previousActiveIndex = Math.max((activeResult.value ?? 0) - 1, 0);
          onResultHover(previousActiveIndex);
          break;
        case 'ArrowDown':
          const nextActiveIndex = activeResult.value !== undefined ? Math.min(activeResult.value + 1, props.results.length - 1) : 0;
          onResultHover(nextActiveIndex);
          break;
        case 'Enter':
          if (activeResult.value !== undefined) {
            onResultClick(activeResult.value);
          }
          break;
      }
    }

    if (e.key === 'Escape') {
      closeSearchBar();
    }
  },
  { target: searchBarRef },
);

watch([isOpen, searchInput], async () => {
  isBodyScrollLocked.value = isOpen.value;

  if (isOpen.value) {
    searchInput.value?.focus();

    if (props.shouldHoverFirstResultOnOpen && props.results?.length) {
      onResultHover(0);
    }

    await nextTick();
    activate();
  } else {
    deactivate();
    value.value = '';
  }
});
</script>

<template>
  <Teleport to="body">
    <CustomTransition name="fade">
      <div
        v-if="isOpen"
        ref="searchBarRef"
        class="fixed inset-x-0 top-0 z-40 flex justify-center max-h-full overflow-y-auto px-4 pt-20 pb-6 max-sm:p-0"
      >
        <div
          v-on-click-outside="onClickOutside"
          class="m-10 z-50 md:max-w-3xl w-full flex flex-col p-6 border rounded-2xl border-neutral-surface bg-neutral-surface shadow-md"
          :class="{
            'gap-6': props.results,
          }"
        >
          <slot name="header"></slot>

          <!-- Search bar -->
          <div v-if="!hideSearchBar" class="flex flex-row items-center gap-3 bg-neutral-surface rounded-2xl">
            <ElIcon name="MagnifyingGlassIcon" class="text-neutral-lighter w-6 h-6" />
            <input
              ref="searchInput"
              v-model="value"
              :name="id || `${uuid}`"
              type="text"
              class="p-0 text-lg font-normal flex-1 outline-none border-none bg-neutral-surface focus:ring-none focus:border-none focus:outline-none border-transparent focus:border-transparent focus:ring-0"
              :placeholder="placeholder"
              @focusin="
                () => {
                  isSearchFocused = true; // Set search focused
                  isClickOutside = false; // Set click outside to false
                }
              "
              @focusout="
                () => {
                  isSearchFocused = false;
                }
              "
            />
          </div>

          <span class="flex flex-col gap-y-6 overflow-y-hidden">
            <ElDivider v-if="results && !hideSearchBar" direction="horizontal" />
            <div
              v-if="blankResults && computedStatus.blank"
              class="flex flex-col items-center justify-center flex-1 gap-4 py-8 text-neutral-lighter"
            >
              <ElIcon v-bind="blankResults.icon" class="w-7 h-7" />
              <p class="text-sm font-semibold text-neutral-darker">{{ blankResults.primaryRow }}</p>
              <p class="text-sm font-normal">{{ blankResults.secondaryRow }}</p>
            </div>
            <div v-if="results && computedStatus.filled" class="flex py-2 overflow-y-hidden">
              <!-- Results Left column  -->
              <div
                class="flex flex-col items-start flex-1 gap-6 overflow-y-auto max-h-50-screen"
                :class="{ 'pr-6': slots.info && results.length > 0 }"
              >
                <span v-if="text" class="text-sm font-medium text-neutral-darker">{{ text }}</span>
                <div class="flex flex-col gap-1 w-full">
                  <ElVerticalTab
                    v-for="(result, index) in results"
                    :key="index"
                    ref="itemRefs"
                    :color="color"
                    v-bind="result"
                    :status="activeResult === index ? 'hover' : 'default'"
                    tabindex="-1"
                    @click="() => onResultClick(index)"
                    @mouseover="() => onResultHover(index)"
                  />
                </div>
              </div>
              <!-- Blank Results -->

              <div v-if="slots.info">
                <ElDivider direction="vertical" />
              </div>
              <div v-if="slots.info" class="flex-1 pl-6 overflow-y-auto">
                <slot name="info"></slot>
              </div>
            </div>
            <div v-if="results && footer" class="flex items-center gap-1 self-stretch">
              <span class="text-sm font-normal text-neutral-darker">{{ footer.text }}</span>
              <ElButton v-bind="footer.button" variant="tertiary" />
            </div>
          </span>
        </div>

        <!-- Backdrop -->
        <div class="fixed inset-0 transition-opacity">
          <div ref="backdrop" class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
      </div>
    </CustomTransition>
  </Teleport>
</template>
